import { useMemo, useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@design-system';
import { FundingPolicyType } from '@shared/appModel';
import { getCumulativeLtcPayoutSeries } from '@shared/calculations/funding/projection/projectionTotals';
import { buildPayoutColumnGroups } from './projectionTable/columnGroups';
import { claimantMonthKey, computeClaimantRelativeMonthMap } from './projectionTable/formatters';
import type { PoolDrawdownToDate } from './projectionTable/types';
import { ProjectionTable } from './projectionTable/ProjectionTable';
import { getPresentation } from './projectionTable/strategies/registry';
import { CollapsibleSection } from './CollapsibleSection';
import { BenefitsBeginTooltip } from './BenefitsBeginAnchor';

type ClaimantView = 'both' | 'client' | 'partner';

interface MonthlyBreakdownTableProps {
  projection: ClaimProjection;
  policyType: FundingPolicyType | null | undefined;
}

/**
 * Single month-by-month table for the projection. Out-of-pocket months and
 * paying months share one table — non-paying rows surface their out-of-pocket
 * reason inside the Policy payout column, and the calc layer tags the first
 * out-of-pocket row with an "Out-of-pocket period begins" milestone.
 */
export function MonthlyBreakdownTable({
  projection,
  policyType,
}: MonthlyBreakdownTableProps) {
  // Joint detection — any tagged row anywhere in the projection switches us
  // into per-claimant mode (per-spouse OOP split + view filter). For
  // single-life every row's `claimant` is undefined and this stays false.
  const isJoint = useMemo(
    () => projection.months.some(m => m.context.claimant != null),
    [projection.months],
  );

  const [view, setView] = useState<ClaimantView>('both');
  const [dimUnchanged, setDimUnchanged] = useState(true);

  // View filter: hidden for single-life; for joint the toggle selects which
  // spouse's rows render.
  const months = useMemo(
    () =>
      view === 'both'
        ? projection.months
        : projection.months.filter(m => m.context.claimant === view),
    [projection.months, view],
  );

  const presentation = useMemo(() => getPresentation(policyType), [policyType]);

  // End-of-PROJECTION state for the Total row, from the UNFILTERED months.
  // When the view filters a joint policy to one claimant, the last visible
  // row can sit mid-projection — the footer's policy-level balances and
  // paid-to-date should still read the true end of the projection.
  const endOfProjection = useMemo(() => {
    const { months: allMonths } = projection;
    const series = getCumulativeLtcPayoutSeries(allMonths);
    return {
      closeStateAtEnd: allMonths[allMonths.length - 1]?.state,
      cumulativeLtcPayoutAtEnd: series[series.length - 1],
    };
  }, [projection]);

  // Cumulative pool drawdown over the full projection, categorized for the
  // "Drawn to date" column. Prior utilization (benefits used before the
  // projection) is an explicit engine output — the amount each strategy
  // actually subtracted from its starting pool(s) — so the column adds it to
  // the cumulative depletion instead of inferring it by subtraction. The old
  // snapshot-subtraction inference fabricated ~⅓ of the pool on joint ShareCare,
  // where the row pool only surfaces the active claimant's accessible capacity.
  const poolDrawdown = useMemo<PoolDrawdownToDate>(() => {
    const priorUtilization = projection.terms.priorUtilization ?? 0;
    const byRowKey = new Map<string, { aob: number; cob: number }>();
    let aob = 0;
    let cob = 0;
    for (const m of projection.months) {
      aob += m.deltas.base.depletion;
      cob += m.deltas.eob.depletion;
      byRowKey.set(claimantMonthKey(m.context.claimant, m.context.monthIndex), { aob, cob });
    }
    return { priorUtilization, byRowKey };
  }, [projection.months, projection.terms]);

  // Per-claimant "Month of LTC" display map for joint projections. Computed
  // from the full projection so partner's first care row reads "Month 1".
  // Single-life is a no-op — every row maps to `monthIndex + 1`.
  const monthDisplayMap = useMemo(
    () => computeClaimantRelativeMonthMap(projection.months),
    [projection.months],
  );

  const payoutGroups = useMemo(
    () =>
      buildPayoutColumnGroups({
        terms: projection.terms,
        yearSnapshots: projection.yearSnapshots,
        presentation,
        poolDrawdown,
        monthDisplayMap,
        // Policy-level running totals (drawn-to-date, cumulative payout, pool
        // remaining) span both spouses even when the view filters to one
        // claimant — flag it so the Drawn column and the Total row state their
        // scope instead of reading as the selected claimant's (P2-47).
        claimantFilteredJointView: isJoint && view !== 'both',
      }),
    [
      projection.terms,
      projection.yearSnapshots,
      presentation,
      poolDrawdown,
      monthDisplayMap,
      isJoint,
      view,
    ],
  );

  return (
    <CollapsibleSection title="Monthly Breakdown" defaultOpen={true}>
      <div className="flex items-center justify-between px-2 pb-4">
        {isJoint ? (
          // Joint-only filter. Radix toggle-group's `single` mode emits an
          // empty string when the active item is re-clicked; coerce that
          // back to 'both' so the user can't end up in a no-selection
          // state that would render an empty table.
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={v => setView((v || 'both') as ClaimantView)}
            variant="outline"
            size="sm"
            aria-label="Show rows for"
          >
            <ToggleGroupItem value="both">Combined</ToggleGroupItem>
            <ToggleGroupItem value="client">Client</ToggleGroupItem>
            <ToggleGroupItem value="partner">Partner</ToggleGroupItem>
          </ToggleGroup>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-4">
          <BenefitsBeginTooltip terms={projection.terms} />
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={dimUnchanged}
              onChange={e => setDimUnchanged(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Dim unchanged values
          </label>
        </div>
      </div>
      <ProjectionTable
        groups={payoutGroups}
        months={months}
        closeStateAtEnd={endOfProjection.closeStateAtEnd}
        cumulativeLtcPayoutAtEnd={endOfProjection.cumulativeLtcPayoutAtEnd}
        dimUnchanged={dimUnchanged}
      />
    </CollapsibleSection>
  );
}
